import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import Imgix from 'react-imgix';
import { useWindowSize } from 'usehooks-ts';

import Highlight from '@components/FeedItems/Highlight';
import IconProTagThumbnail from '@components/Images/Rebranding/IconProTagThumbnail';
import MaybeLink from '@components/MaybeLink';
import { useAppContext } from '@contexts/AppContext';
import { article_path, briefing_path } from '@routes';
import { IMGIX_ENV, IMGIX_URL } from '@src/env';
import { WIDTH_MD, WIDTH_SM } from '@utils/constants';
import { FeedContentType } from '@utils/enums';
import { getArticleImgSrc } from '@utils/images';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import AuthorsLine, { ArticleAuthors } from '../AuthorsLine';
import styles from './styles.module.scss';

export type ArticleOrBriefing = {
  authors: ArticleAuthors;
  commentsCount: number;
  dek?: string;
  excerpt: { short: string; long: string };
  guid: string;
  headline?: string;
  highlight: string;
  highlightSlug?: string;
  id: number;
  isPro?: boolean;
  picture: string | null;
  publishOnWeb: boolean;
  publishedAt?: string;
  slug: string;
  title?: string;
  type: 'Article' | 'Briefing';
};

interface ArticleOrBriefingFeedItemProps {
  articleOrBriefing: ArticleOrBriefing;
  useLargeImage?: boolean;
  useFeaturedLayout?: boolean;
}

export default function ArticleOrBriefingFeedItem({
  articleOrBriefing,
  useLargeImage,
  useFeaturedLayout,
}: ArticleOrBriefingFeedItemProps) {
  const {
    env: { IMGIX_ENV: SSRImgixEnv, IMGIX_URL: SSRImgixUrl },
  } = useAppContext();
  const [imgWidth, setImgWidth] = useState(270);
  const { width } = useWindowSize();

  const showFeaturedLayout = useFeaturedLayout && width < WIDTH_SM;

  useEffect(() => {
    // only show featured image on smaller screens, and set to screen size
    if (showFeaturedLayout) {
      setImgWidth(width);
    } else if (useLargeImage) {
      // standard img is bigger on pages like the search page
      setImgWidth(355);
    } else {
      // standard img is smaller on pages like directory page
      setImgWidth(270);
    }
  }, [showFeaturedLayout, useLargeImage, width]);

  const type = articleOrBriefing.type;
  const articlePath = articleOrBriefing.publishOnWeb
    ? article_path(articleOrBriefing.slug)
    : undefined;

  const href =
    type === FeedContentType.Article ? articlePath : briefing_path(articleOrBriefing.slug);
  return (
    <div className={clsx(styles.article, { [styles.featured]: showFeaturedLayout })}>
      <div
        className={clsx(styles.feedItem, {
          [styles.featured]: showFeaturedLayout,
        })}
        data-test="article-or-briefing-feed-item"
      >
        <div className={styles.content}>
          <div className={styles.details}>
            {!!articleOrBriefing.highlight && articleOrBriefing.highlightSlug && (
              <Highlight highlight={articleOrBriefing.highlight} />
            )}
          </div>
          <MaybeLink
            href={href}
            className="no-link track-click article-link relative"
            id={`article-${articleOrBriefing.id}`}
            data-type="link"
            data-name="article title"
            data-location="feed"
          >
            <div className={styles.title}>
              {articleOrBriefing.title || articleOrBriefing.headline}
            </div>
          </MaybeLink>
          <AuthorsLine
            authors={articleOrBriefing.authors}
            date={articleOrBriefing.publishedAt}
            commentsCount={articleOrBriefing.commentsCount}
            slug={articleOrBriefing.slug}
          />
          {width >= WIDTH_SM && (
            <div className={styles.excerpt}>
              <MaybeLink href={href}>
                {AuthoredContentReactToHtmlParser(
                  width >= WIDTH_MD
                    ? articleOrBriefing.excerpt.long
                    : articleOrBriefing.excerpt.short
                )}
              </MaybeLink>
            </div>
          )}
        </div>
        <div
          className={clsx(styles.image, {
            [styles.imageLarge]: useLargeImage,
          })}
        >
          <MaybeLink
            href={href}
            className="track-link relative"
            id={`article-image-${articleOrBriefing.id}`}
            data-type="link"
            data-name="article image"
            data-location="feed"
          >
            <Imgix
              src={getArticleImgSrc(articleOrBriefing.id, articleOrBriefing.picture, {
                IMGIX_ENV: SSRImgixEnv ?? IMGIX_ENV,
                IMGIX_URL: SSRImgixUrl ?? IMGIX_URL,
              })}
              width={imgWidth}
              height={(imgWidth * 9) / 16}
              imgixParams={{ fit: 'crop', 'gif-q': '50' }}
              className="article-image max-xs:max-w-full"
            />
            {articleOrBriefing.isPro && <IconProTagThumbnail className="absolute right-0 top-0" />}
          </MaybeLink>
        </div>
      </div>
    </div>
  );
}
